import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { companyAPI } from '../../services/api';
import Header from '../Header';
import Footer from '../Footer';
import './CompanySetupPage.css';

const CompanySetupPage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    description: '',
    website: '',
    industry: '',
    company_size: '',
    founded_year: '',
    location: '',
    country_code: '',
    address: '',
    phone: '',
    email: '',
    government_business_id: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name?.trim()) {
      setError('Company name is required.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description?.trim() || null,
        website: form.website?.trim() || null,
        industry: form.industry?.trim() || null,
        company_size: form.company_size?.trim() || null,
        founded_year: form.founded_year ? parseInt(form.founded_year, 10) : null,
        location: form.location?.trim() || null,
        country_code: form.country_code?.trim() || null,
        address: form.address?.trim() || null,
        phone: form.phone?.trim() || null,
        email: form.email?.trim() || null,
        government_business_id: form.government_business_id?.trim() || null,
      };
      const res = await companyAPI.setup(payload);
      if (res.success && res.data) {
        if (res.data.user) {
          updateUser(res.data.user);
        }
        // Open Post Job modal on profile so the new company appears in the list; user can switch from there
        navigate('/profile', { replace: true, state: { openPostJobChoice: true } });
        return;
      }
      setError(res.message || 'Failed to create company.');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.data?.message || err?.message || 'Failed to create company.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App">
      <Header
        isLoggedIn={!!user}
        user={user}
        onProfileClick={() => navigate('/profile')}
        onAccountSettingsClick={() => navigate('/settings')}
        onHomeClick={() => navigate('/')}
        onJobsClick={() => navigate('/vacancies')}
      />
    <div className="company-setup-page">
      <div className="company-setup-card">
        <h1 className="company-setup-title">Set up your company</h1>
        <p className="company-setup-intro">Add basic information. You can edit this later from your company profile.</p>

        <form onSubmit={handleSubmit} className="company-setup-form">
          {error && <div className="company-setup-error">{error}</div>}

          <div className="company-setup-field">
            <label htmlFor="name">Company name <span className="required">*</span></label>
            <input
              id="name"
              name="name"
              type="text"
              value={form.name}
              onChange={handleChange}
              placeholder="e.g. Acme Inc."
              maxLength={255}
              required
            />
          </div>

          <div className="company-setup-field">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="What does your company do?"
              rows={3}
            />
          </div>

          <div className="company-setup-field">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              name="website"
              type="url"
              value={form.website}
              onChange={handleChange}
              placeholder="https://..."
            />
          </div>

          <div className="company-setup-row">
            <div className="company-setup-field">
              <label htmlFor="industry">Industry</label>
              <input
                id="industry"
                name="industry"
                type="text"
                value={form.industry}
                onChange={handleChange}
                placeholder="e.g. Technology"
              />
            </div>
            <div className="company-setup-field">
              <label htmlFor="company_size">Company size</label>
              <select
                id="company_size"
                name="company_size"
                value={form.company_size}
                onChange={handleChange}
              >
                <option value="">Select size</option>
                <option value="1-10">1-10</option>
                <option value="11-50">11-50</option>
                <option value="51-200">51-200</option>
                <option value="201-500">201-500</option>
                <option value="500+">500+</option>
              </select>
            </div>
          </div>

          <div className="company-setup-row">
            <div className="company-setup-field">
              <label htmlFor="founded_year">Founded year</label>
              <input
                id="founded_year"
                name="founded_year"
                type="number"
                min="1800"
                max={new Date().getFullYear()}
                value={form.founded_year}
                onChange={handleChange}
                placeholder="Year"
              />
            </div>
            <div className="company-setup-field">
              <label htmlFor="location">Location</label>
              <input
                id="location"
                name="location"
                type="text"
                value={form.location}
                onChange={handleChange}
                placeholder="City or region"
              />
            </div>
          </div>

          <div className="company-setup-field">
            <label htmlFor="address">Address</label>
            <input
              id="address"
              name="address"
              type="text"
              value={form.address}
              onChange={handleChange}
              placeholder="Street address"
            />
          </div>

          <div className="company-setup-row">
            <div className="company-setup-field">
              <label htmlFor="phone">Phone</label>
              <input
                id="phone"
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                placeholder="+1 234 567 8900"
              />
            </div>
            <div className="company-setup-field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="contact@company.com"
              />
            </div>
          </div>

          <div className="company-setup-field">
            <label htmlFor="government_business_id">Government business ID</label>
            <input
              id="government_business_id"
              name="government_business_id"
              type="text"
              value={form.government_business_id}
              onChange={handleChange}
              placeholder="e.g. Tax ID, registration number"
            />
          </div>

          <div className="company-setup-actions">
            <button
              type="button"
              className="company-setup-btn secondary"
              onClick={() => navigate(-1)}
              disabled={submitting}
            >
              Back
            </button>
            <button type="submit" className="company-setup-btn primary" disabled={submitting}>
              {submitting ? (
                <>
                  <span className="spinner" />
                  Creating...
                </>
              ) : (
                'Create company & continue'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
    <Footer />
    </div>
  );
};

export default CompanySetupPage;
